import * as PropTypes from 'prop-types';

import {
  AsyncValidationStatus as SchemaValidationStatus,
  ValidationErrorLabel,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SCHEMA_FIELD_SHAPE, SchemaField} from 'schemas/fields/SchemaFieldConstants';

// Keep in sync with benchling/schemas/models/schema_validation_state.py:SchemaValidationStatus
export {SchemaValidationStatus};

export enum SchemaValidationShortDisplayTextByStatus {
  FAILED = 'Failed',
  VALIDATING = 'Validating',
  PASSED = 'Passed',
}

export enum SchemaValidationLongDisplayTextByStatus {
  FAILED = 'Failed validation checks',
  OVERRIDDEN = 'Validation checks were overridden',
  VALIDATING = 'Running validation checks...',
}

const SCHEMA_VALIDATION_STATUSES: Array<string> = [
  SchemaValidationStatus.PASSED,
  SchemaValidationStatus.OVERRIDDEN,
  SchemaValidationStatus.FAILED,
  SchemaValidationStatus.VALIDATING,
];

export const SCHEMA_VALIDATION_STATE_SHAPE = PropTypes.shape({
  status: PropTypes.oneOf(SCHEMA_VALIDATION_STATUSES).isRequired,
});

export interface SchemaValidationState {
  status: SchemaValidationStatus;
}

// Keep in sync with benchling/sightglass2/filters/schemas/schema_validation_error_label_filter.py
export enum SchemaValidationErrorLabel {
  CONSTRAINT_VIOLATION = ValidationErrorLabel.CONSTRAINT_VIOLATION,
  CONFIGURABLE_VALIDATION_RULE_FAILURE = ValidationErrorLabel.CONFIGURABLE_VALIDATION_RULE_FAILURE,
  INVALID_LINK = ValidationErrorLabel.INVALID_LINK,
  INVALID_MULTIPLE_LINK = ValidationErrorLabel.INVALID_MULTIPLE_LINK,
  INVALID_VALUE = ValidationErrorLabel.INVALID_VALUE,
  MISSING_REQUIRED = ValidationErrorLabel.MISSING_REQUIRED,
  OTHER = 'OTHER',
}

const schemaValidationErrorLabelToShortDisplayText = {
  [SchemaValidationErrorLabel.CONSTRAINT_VIOLATION]: 'Constraint violation',
  [SchemaValidationErrorLabel.CONFIGURABLE_VALIDATION_RULE_FAILURE]: 'Failed conditions',
  [SchemaValidationErrorLabel.INVALID_LINK]: 'Invalid entity link',
  [SchemaValidationErrorLabel.INVALID_VALUE]: 'Invalid field value',
  [SchemaValidationErrorLabel.INVALID_MULTIPLE_LINK]: 'Invalid multi-link',
  [SchemaValidationErrorLabel.MISSING_REQUIRED]: 'Missing required',
  [SchemaValidationErrorLabel.OTHER]: 'Other',
};
export function shortDisplayTextForSchemaValidationErrorLabel(label: SchemaValidationErrorLabel): string {
  return schemaValidationErrorLabelToShortDisplayText[label];
}

const schemaValidationErrorLabelToLongDisplayText = {
  [SchemaValidationErrorLabel.CONSTRAINT_VIOLATION]: 'Constraint violation',
  [SchemaValidationErrorLabel.CONFIGURABLE_VALIDATION_RULE_FAILURE]: 'Failed conditions',
  [SchemaValidationErrorLabel.INVALID_LINK]: 'Invalid entity link',
  [SchemaValidationErrorLabel.INVALID_VALUE]: 'Invalid field value',
  [SchemaValidationErrorLabel.INVALID_MULTIPLE_LINK]: 'Multiple links not allowed',
  [SchemaValidationErrorLabel.MISSING_REQUIRED]: 'Missing required value',
  [SchemaValidationErrorLabel.OTHER]: 'Other',
};
export function longDisplayTextForSchemaValidationErrorLabel(label: SchemaValidationErrorLabel): string {
  return schemaValidationErrorLabelToLongDisplayText[label];
}

// Keep in sync with benchling/schemas/models/schema_validation_error_record.py:to_dict
export interface SchemaValidationErrorRecord {
  errorLabel: string;
  field?: SchemaField | null;
  message: string;
}

export const SCHEMA_VALIDATION_ERROR_RECORD_SHAPE = PropTypes.shape({
  errorLabel: PropTypes.string.isRequired,
  field: SCHEMA_FIELD_SHAPE,
  message: PropTypes.string.isRequired,
});

// Keep in sync with benchling/registry/validation/validation_extras.py:get_validation_extras_dict
export interface ValidationExtras {
  errorRecords: Array<SchemaValidationErrorRecord> | null;
}

export const VALIDATION_EXTRAS_SHAPE = PropTypes.shape({
  errorRecords: PropTypes.arrayOf(SCHEMA_VALIDATION_ERROR_RECORD_SHAPE),
});
